import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronRight, Layers, Star, Zap } from 'lucide-react';

import {
  FEATURES,
  GameMock,
  NAV_ITEMS,
  SOCIAL_PROOF_SEEDS,
  STAR_KEYS,
  ZONE_LABELS,
} from './landingPageSections';
import { LandingPageCta, LandingPageFooter } from './landingPageFooter';
import {
  LandingPageAgentSystem,
  LandingPageLevelEditor,
  LandingPageNpcShowcase,
} from './landingPageShowcase';

const LOGO_SRC = '/assets/logo/waysprite_new_logo_cropped_transparent.svg';

export function LandingPage(): JSX.Element {
  const navigate = useNavigate();
  const handlePlayNow = useCallback(() => void navigate('/app'), [navigate]);
  const [scrolled, setScrolled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });
  const heroY = useTransform(scrollYProgress, [0, 0.15], [0, -40]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div ref={containerRef} className="relative min-h-screen bg-[#050b1a] font-sans text-sky-100">
      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav
        className={`fixed top-0 z-50 w-full transition-all duration-300 ${
          scrolled
            ? 'border-b border-[#0ea5e9]/15 bg-[#050b1a]/70 py-3 backdrop-blur-2xl'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <img alt="Waysprite" height={38} src={LOGO_SRC} width={38} />
            <span className="text-xl font-black tracking-tight text-white">Waysprite</span>
          </div>
          <div className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                className="rounded-lg px-4 py-2 text-sm text-sky-300 transition-colors hover:bg-sky-500/10 hover:text-white"
                href={`#${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              className="hidden px-4 py-2 text-sm text-sky-300 transition-colors hover:text-white sm:block"
              disabled
              title="Coming soon"
              type="button"
            >
              Sign in
            </button>
            <button
              className="rounded-xl bg-gradient-to-r from-[#1d4ed8] to-[#0ea5e9] px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-[#0ea5e9]/25 transition-all hover:from-[#2563eb] hover:to-[#38bdf8] hover:shadow-[#38bdf8]/30"
              onClick={handlePlayNow}
              type="button"
            >
              Play Beta
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pb-16 pt-28">
        <div className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-[#1d4ed8]/10 blur-[130px]" />
        <div className="pointer-events-none absolute right-0 top-20 h-[400px] w-[400px] rounded-full bg-[#06b6d4]/8 blur-[100px]" />
        <div className="pointer-events-none absolute bottom-0 left-0 h-40 w-full bg-gradient-to-t from-[#050b1a] to-transparent" />

        <div className="mx-auto max-w-7xl px-5">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ y: heroY, opacity: heroOpacity }}
            className="flex flex-col items-start"
          >
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#0ea5e9]/30 bg-gradient-to-r from-[#1d4ed8]/15 to-[#06b6d4]/15 px-3 py-1.5 text-xs font-bold tracking-wide text-[#7dd3fc] backdrop-blur-sm">
              <Zap className="h-3 w-3 text-[#22d3ee]" />
              <span>LIVE MULTIPLAYER &middot; AI-NATIVE &middot; BROWSER-BASED</span>
            </div>

            <div className="grid w-full grid-cols-1 items-center gap-14 lg:grid-cols-[1fr_1.1fr]">
              <div>
                <h1 className="mb-6 text-5xl font-black leading-[1.0] tracking-tight text-white lg:text-[68px]">
                  <span>A world where</span>
                  <br />
                  <span className="bg-gradient-to-r from-[#38bdf8] to-[#22d3ee] bg-clip-text text-transparent">
                    every NPC
                  </span>
                  <br />
                  <span>has a mind.</span>
                </h1>
                <p className="mb-8 max-w-lg text-lg leading-relaxed text-sky-300/80">
                  Waysprite is a 2D top-down multiplayer sandbox powered by Phaser 3, Gemini, and
                  Claude. Create avatars, explore hand-crafted zones, and hold real conversations
                  with AI characters who actually remember you.
                </p>
                <div className="mb-10 flex flex-wrap gap-3">
                  <button
                    className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#1d4ed8] to-[#0ea5e9] px-7 py-3.5 text-[15px] font-bold text-white shadow-xl shadow-[#0ea5e9]/25 transition-all hover:from-[#2563eb] hover:to-[#38bdf8]"
                    onClick={handlePlayNow}
                    type="button"
                  >
                    <span>Start Exploring</span>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    className="rounded-xl border border-[#1e3a5f] bg-[#071428]/80 px-7 py-3.5 text-[15px] font-bold text-sky-200 backdrop-blur-sm transition-all hover:border-[#0ea5e9]/50"
                    type="button"
                  >
                    View Docs
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-2.5">
                    {SOCIAL_PROOF_SEEDS.map((s) => (
                      <div
                        key={s.label}
                        className="h-9 w-9 overflow-hidden rounded-full border-2 border-[#050b1a] bg-[#0a1a30]"
                      >
                        <img
                          alt="Player avatar"
                          className="h-full w-full"
                          src={`https://api.dicebear.com/7.x/pixel-art/svg?seed=${s.label}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="mb-0.5 flex items-center gap-1.5">
                      {STAR_KEYS.map((s) => (
                        <Star key={s.label} className="h-3 w-3 fill-[#22d3ee] text-[#22d3ee]" />
                      ))}
                    </div>
                    <p className="text-xs text-sky-600">
                      <span className="font-semibold text-sky-200">1,240+ players</span> exploring
                      right now
                    </p>
                  </div>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <GameMock />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Zone strip ─────────────────────────────────────────────────── */}
      <div className="overflow-hidden border-y border-[#0ea5e9]/10 bg-[#071428]/60 py-4 backdrop-blur-sm">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-10 whitespace-nowrap px-6">
            {ZONE_LABELS.map((zone) => (
              <span
                key={zone.label}
                className="flex items-center gap-2 text-sm font-medium text-sky-600"
              >
                <span>{zone.label}</span>
                <span className="text-[#1e3a5f]">&middot;</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <section className="py-24" id="explore">
        <div className="mx-auto max-w-7xl px-5">
          <div className="mb-14">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#06b6d4]/30 bg-[#06b6d4]/12 px-3 py-1.5 text-xs font-bold tracking-wide text-[#67e8f9] backdrop-blur-sm">
              <Layers className="h-3 w-3" />
              <span>THE PLATFORM</span>
            </div>
            <h2 className="max-w-xl text-4xl font-black leading-tight tracking-tight text-white">
              Everything you need to build living worlds
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-[#0ea5e9]/10 bg-[#0ea5e9]/10 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <motion.div
                key={f.title}
                whileHover={{ backgroundColor: 'rgba(14,165,233,0.07)' }}
                className="group flex flex-col gap-4 bg-[#071428]/80 p-7 backdrop-blur-sm transition-colors"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#22d3ee]/30 bg-[#06b6d4]/15 transition-colors group-hover:border-[#22d3ee]/60">
                  <f.icon className="h-5 w-5 text-[#22d3ee]" />
                </div>
                <h3 className="text-base font-bold text-white">{f.title}</h3>
                <p className="text-sm leading-relaxed text-sky-400/70">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <LandingPageNpcShowcase />

      <LandingPageAgentSystem />

      <LandingPageLevelEditor />

      <LandingPageCta onPlayNow={handlePlayNow} />

      <LandingPageFooter />
    </div>
  );
}
